import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

export default function Metrics({ targetUserId, hideHeader, snapshotInline }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [metrics, setMetrics] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newMetric, setNewMetric] = useState({ key: "", label_en: "", label_fr: "", unit: "" });
  const [err, setErr] = useState("");
  const [snaps, setSnaps] = useState([]);
  const [snapBusy, setSnapBusy] = useState(false);

  const isOwn = !targetUserId || targetUserId === user?.id;

  const load = () => {
    const q = !isOwn ? `?user_id=${targetUserId}` : "";
    return api.get(`/metrics${q}`).then((r) => setMetrics(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  const loadSnaps = () => {
    const q = !isOwn ? `?user_id=${targetUserId}` : "";
    api.get(`/snapshots/metrics${q}`).then((r) => setSnaps(r.data)).catch(() => setSnaps([]));
  };
  useEffect(() => { load(); loadSnaps(); }, [targetUserId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!metrics || !user) return <div className="tb-loading">{t("common.loading")}</div>;
  const editable = isOwn && user.role === "entrepreneur";

  const updateValue = async (m, value) => {
    setMetrics((cur) => cur.map((x) => (x.id === m.id ? { ...x, value } : x)));
    try {
      await api.put(`/metrics/${m.id}`, { value });
    } catch (e) {
      /* noop */
    }
  };

  const removeMetric = async (m) => {
    if (!window.confirm(`${t("metrics.confirmDelete")} ${labelOf(m)}?`)) return;
    try {
      await api.delete(`/metrics/${m.id}`);
      setMetrics((cur) => cur.filter((x) => x.id !== m.id));
    } catch (e) {
      /* noop */
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const slug = newMetric.key.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_");
      const { data } = await api.post("/metrics", { ...newMetric, key: slug });
      setMetrics((cur) => [...cur, data]);
      setNewMetric({ key: "", label_en: "", label_fr: "", unit: "" });
      setAdding(false);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  const labelOf = (m) => (lang === "fr" ? m.label_fr || m.label_en : m.label_en);
  const baseMetrics = metrics.filter((m) => m.category === "base");
  const customMetrics = metrics.filter((m) => m.category === "custom");

  return (
    <div className="tb-page">
      {!hideHeader && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: "1rem" }}>
            <div>
              <h1 className="tb-h1" data-testid="metrics-heading">{t("metrics.title")}</h1>
              <p className="tb-muted" style={{ marginBottom: 0 }}>{t("metrics.subtitle")}</p>
            </div>
            <Link to="/app/metric-groups" className="tb-ghost-btn" data-testid="open-metric-groups">{t("metrics.openGroups")}</Link>
          </div>
        </>
      )}

      {(editable || snapshotInline) && (
        <SnapshotBar
          kind="metrics"
          snaps={snaps}
          canSave={editable}
          busy={snapBusy}
          t={t}
          userId={isOwn ? undefined : targetUserId}
          onSave={async () => {
            setSnapBusy(true);
            try { await api.post("/snapshots/metrics"); loadSnaps(); } finally { setSnapBusy(false); }
          }}
        />
      )}

      <div className="tb-block" style={{ marginBottom: "1.25rem" }}>
        <h3 style={{ fontFamily: "var(--font-display)", margin: "0 0 0.8rem" }}>{t("metrics.coreMetrics")}</h3>
        <div className="tb-metric-grid" data-testid="metrics-grid">
          {baseMetrics.map((m) => (
            <MetricCard key={m.id} m={m} label={labelOf(m)} editable={editable} onUpdate={updateValue} onRemove={removeMetric} />
          ))}
        </div>
      </div>

      {(customMetrics.length > 0 || editable) && (
        <div className="tb-block">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <h3 style={{ fontFamily: "var(--font-display)", margin: 0 }}>{t("metrics.customMetrics")}</h3>
            {editable && !adding && (
              <button className="tb-primary-btn" style={{ padding: "6px 14px", fontSize: "0.82rem" }} onClick={() => setAdding(true)} data-testid="btn-add-metric">
                + {t("metrics.add")}
              </button>
            )}
          </div>
          {adding && (
            <form onSubmit={submitNew} className="tb-block" style={{ background: "var(--surface)", marginBottom: "1rem", padding: "1rem" }} data-testid="add-metric-form">
              <div className="tb-row">
                <div>
                  <label>{t("metrics.label")} (EN)</label>
                  <input type="text" required value={newMetric.label_en} onChange={(e) => setNewMetric({ ...newMetric, label_en: e.target.value })} data-testid="new-metric-label-en" />
                </div>
                <div>
                  <label>{t("metrics.label")} (FR)</label>
                  <input type="text" value={newMetric.label_fr} onChange={(e) => setNewMetric({ ...newMetric, label_fr: e.target.value })} data-testid="new-metric-label-fr" />
                </div>
              </div>
              <div className="tb-row">
                <div>
                  <label>{t("metrics.key")}</label>
                  <input type="text" required value={newMetric.key} onChange={(e) => setNewMetric({ ...newMetric, key: e.target.value })} placeholder="e.g. nps_score" data-testid="new-metric-key" />
                </div>
                <div>
                  <label>{t("metrics.unit")}</label>
                  <input type="text" value={newMetric.unit} onChange={(e) => setNewMetric({ ...newMetric, unit: e.target.value })} placeholder="$, %, #" data-testid="new-metric-unit" />
                </div>
              </div>
              {err && <div className="tb-form-error" style={{ marginTop: "0.6rem" }}>{err}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: "0.6rem" }}>
                <button type="button" className="tb-ghost-btn" onClick={() => { setAdding(false); setErr(""); }}>
                  {t("book.cancel")}
                </button>
                <button type="submit" className="tb-primary-btn" data-testid="submit-new-metric">{t("metrics.add")}</button>
              </div>
            </form>
          )}
          {customMetrics.length === 0 && !adding && <p className="tb-muted">{t("metrics.noCustom")}</p>}
          <div className="tb-metric-grid">
            {customMetrics.map((m) => (
              <MetricCard key={m.id} m={m} label={labelOf(m)} editable={editable} onUpdate={updateValue} onRemove={removeMetric} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ m, label, editable, onUpdate, onRemove }) {
  return (
    <div className="tb-metric-card" data-testid={`metric-${m.key}`}>
      <div className="tb-metric-label">
        <span>{label}</span>
        {editable && m.category === "custom" && (
          <button onClick={() => onRemove(m)} className="tb-metric-del" aria-label="delete" data-testid={`metric-del-${m.key}`}>
            ×
          </button>
        )}
      </div>
      <div className="tb-metric-value">
        {editable ? (
          <input
            type="text"
            value={m.value || ""}
            onChange={(e) => onUpdate(m, e.target.value)}
            placeholder="—"
            data-testid={`metric-input-${m.key}`}
          />
        ) : (
          <span>{m.value || "—"}</span>
        )}
        {m.unit && <small className="tb-muted">{m.unit}</small>}
      </div>
    </div>
  );
}

export function SnapshotBar({ kind, snaps, canSave, busy, t, onSave, userId }) {
  const latest = snaps && snaps.length > 0 ? snaps[0] : null;
  const historyUrl = userId
    ? `/app/snapshots/${kind}?user_id=${encodeURIComponent(userId)}`
    : `/app/snapshots/${kind}`;
  const latestLabel = latest
    ? [latest.created_by_name, new Date(latest.created_at).toLocaleString()].filter(Boolean).join(" · ")
    : t("snapshot.noneYet");
  return (
    <div
      className="tb-block"
      style={{ marginBottom: "1.25rem", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}
      data-testid={`snapshot-bar-${kind}`}
    >
      <div>
        <div className="tb-muted" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {t(`snapshot.${kind}Label`)}
        </div>
        <div style={{ fontWeight: 600, marginTop: 4 }} data-testid={`snapshot-latest-${kind}`}>
          {latestLabel}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Link to={historyUrl} className="tb-ghost-btn" style={{ textDecoration: "none", padding: "8px 14px" }} data-testid={`snapshot-history-${kind}`}>
          {t("snapshot.viewHistory")}
        </Link>
        {canSave && (
          <button className="tb-primary-btn" disabled={busy} onClick={onSave} data-testid={`snapshot-save-${kind}`}>
            {busy ? t("auth.loading") : t("snapshot.saveSubmit")}
          </button>
        )}
      </div>
    </div>
  );
}
